import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Toaster } from "@/components/ui/sonner";

import Landing from "@/pages/Landing";
import BrowseClinics from "@/pages/BrowseClinics";
import ClinicLanding from "@/pages/ClinicLanding";
import PatientLogin from "@/pages/PatientLogin";
import PatientRegister from "@/pages/PatientRegister";
import PatientDashboard from "@/pages/PatientDashboard";
import PatientRewards from "@/pages/PatientRewards";
import PatientProfile from "@/pages/PatientProfile";
import PatientFamily from "@/pages/PatientFamily";
import PatientReferrals from "@/pages/PatientReferrals";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminPatients from "@/pages/AdminPatients";
import AdminRewardsPage from "@/pages/AdminRewardsPage";
import AdminTiers from "@/pages/AdminTiers";
import AdminRules from "@/pages/AdminRules";
import AdminCampaigns from "@/pages/AdminCampaigns";
import AdminBranding from "@/pages/AdminBranding";
import AdminCheckin from "@/pages/AdminCheckin";
import SuperAdminLogin from "@/pages/SuperAdminLogin";
import SuperAdminDashboard from "@/pages/SuperAdminDashboard";
import SuperAdminClinics from "@/pages/SuperAdminClinics";
import SuperAdminApplications from "@/pages/SuperAdminApplications";
import SuperAdminContent from "@/pages/SuperAdminContent";
import SuperAdminWallet from "@/pages/SuperAdminWallet";
import ClinicRegister from "@/pages/ClinicRegister";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import LegalPage from "@/pages/LegalPage";
import AuthCallback from "@/pages/AuthCallback";

function ProtectedRoute({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-mr-secondary">Loading…</div>;
  if (!user) return <Navigate to="/" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function AppRoutes() {
  const location = useLocation();
  // CRITICAL: handle Emergent OAuth callback synchronously
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/clinics" element={<BrowseClinics />} />
      <Route path="/about" element={<About />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/register-clinic" element={<ClinicRegister />} />
      <Route path="/privacy" element={<LegalPage section="privacy" />} />
      <Route path="/terms" element={<LegalPage section="terms" />} />

      {/* Tenant scoped patient routes */}
      <Route path="/c/:slug" element={<ClinicLanding />} />
      <Route path="/c/:slug/login" element={<PatientLogin />} />
      <Route path="/c/:slug/register" element={<PatientRegister />} />
      <Route path="/c/:slug/dashboard" element={<ProtectedRoute roles={["patient"]}><PatientDashboard /></ProtectedRoute>} />
      <Route path="/c/:slug/rewards" element={<ProtectedRoute roles={["patient"]}><PatientRewards /></ProtectedRoute>} />
      <Route path="/c/:slug/profile" element={<ProtectedRoute roles={["patient"]}><PatientProfile /></ProtectedRoute>} />
      <Route path="/c/:slug/family" element={<ProtectedRoute roles={["patient"]}><PatientFamily /></ProtectedRoute>} />
      <Route path="/c/:slug/refer" element={<ProtectedRoute roles={["patient"]}><PatientReferrals /></ProtectedRoute>} />

      {/* Admin */}
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={<ProtectedRoute roles={["admin", "staff"]}><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/patients" element={<ProtectedRoute roles={["admin", "staff"]}><AdminPatients /></ProtectedRoute>} />
      <Route path="/admin/rewards" element={<ProtectedRoute roles={["admin"]}><AdminRewardsPage /></ProtectedRoute>} />
      <Route path="/admin/tiers" element={<ProtectedRoute roles={["admin"]}><AdminTiers /></ProtectedRoute>} />
      <Route path="/admin/rules" element={<ProtectedRoute roles={["admin"]}><AdminRules /></ProtectedRoute>} />
      <Route path="/admin/campaigns" element={<ProtectedRoute roles={["admin"]}><AdminCampaigns /></ProtectedRoute>} />
      <Route path="/admin/branding" element={<ProtectedRoute roles={["admin"]}><AdminBranding /></ProtectedRoute>} />
      <Route path="/admin/checkin" element={<ProtectedRoute roles={["admin", "staff"]}><AdminCheckin /></ProtectedRoute>} />

      {/* Super Admin */}
      <Route path="/super-admin/login" element={<SuperAdminLogin />} />
      <Route path="/super-admin" element={<ProtectedRoute roles={["super_admin"]}><SuperAdminDashboard /></ProtectedRoute>} />
      <Route path="/super-admin/clinics" element={<ProtectedRoute roles={["super_admin"]}><SuperAdminClinics /></ProtectedRoute>} />
      <Route path="/super-admin/applications" element={<ProtectedRoute roles={["super_admin"]}><SuperAdminApplications /></ProtectedRoute>} />
      <Route path="/super-admin/content" element={<ProtectedRoute roles={["super_admin"]}><SuperAdminContent /></ProtectedRoute>} />
      <Route path="/super-admin/wallet" element={<ProtectedRoute roles={["super_admin"]}><SuperAdminWallet /></ProtectedRoute>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-right" richColors closeButton />
      </BrowserRouter>
    </AuthProvider>
  );
}
